import React, { useState, useEffect } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  ADD_COURSE_LABEL,
  CANCEL_LABEL,
  CATEGORY_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  CONFIRM_LABEL,
  COURSES_LABEL,
  DESCRIPTION_LABEL,
  EXECUTION_MODE_LABEL,
  GROUP_TARGETS_LABEL,
  NAME_LABEL,
  URL_LABEL,
  EMPTY_COURSES_MESSAGE,
  COURSE_LABEL,
  RECIPIENT_LABEL,
  EMPTY_STRING,
} from "../../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../../types/AppTypes";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { Course } from "../../../../../../../types/api/courses.type";
import { TableFormationCM } from "../../TableFormationCM/TableFormationCM";
import { StyledFormationCM } from "../../TableFormationCM/TableFormationCM.styled";
import { DoubleStructure } from "../../../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import SearchInput from "../../../../../../../components/core/SearchInput/SearchInput";
import {
  TextBoxGrid,
  TextBox,
} from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { CollaboratorCmSheet } from "../../../../../../../types/api/collaborators.type";
import { getCollaboratorCoursesModalColumns } from "../../../../../../../constants/TableColumns";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import { findLabelFromValue } from "../../../../../../../utils/orgFunctions.utils";
import { COURSES_PAGE_SIZE } from "../../../../../../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import TargetGroups from "../../../../../../../components/TargetGroups/TargetGroups";
import { useCmSheetAvailableCourses } from "../../../../../../../hooks/cmSheet/courses.hook";

interface AddCollaboratorCoursesProps {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: (courses: Course[]) => void;
  cmSheetInfo: CollaboratorCmSheet;
}

export const AddCollaboratorCourses = ({
  isOpen,
  closeModal,
  onConfirm,
  cmSheetInfo,
}: AddCollaboratorCoursesProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [coursesSaved, setCoursesSaved] = useState<Course[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(0);

  const { orgSettings } = useAppSelector((state) => state.config);

  const confirmModal = (courses: Course[]) => {
    onConfirm(courses);
    closeModal();
  };

  const handleCloseModal = () => {
    setSelectedCategories([]);
    closeModal();
    setCoursesSaved([]);
  };

  const { data, isFetching } = useCmSheetAvailableCourses({
    cmSheetId: cmSheetInfo.id,
    pageNumber,
    selectedCategories,
    searchDebouncedValue,
  });

  const handleRowSelect = (course: Course, checked: boolean) => {
    setCoursesSaved((prev) => {
      if (checked) {
        return prev.some((i) => i.id === course.id)
          ? prev
          : [
              ...prev,
              {
                ...course,
              },
            ];
      }
      return prev.filter((i) => i.id !== course.id);
    });
  };

  const handleSelectAll = (rows: Course[], coursesSaved: boolean) => {
    if (!coursesSaved) {
      const pageIds = new Set(rows.map((r) => r.id));
      setCoursesSaved((prev) => prev.filter((c) => !pageIds.has(c.id)));
      return;
    }
    setCoursesSaved((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      return [...prev, ...rows.filter((r) => !existingIds.has(r.id))];
    });
  };

  const expandRow = (record: Course): React.ReactElement | null => {
    return (
      <>
        {!record.adHocCollaborator && (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: GROUP_TARGETS_LABEL,
                text: <TargetGroups cc={record.cc} />,
              },
              {
                label: INTERNAL_GRADE_IT_LABEL,
                text: record.internalGrade
                  ? record.internalGrade.join(", ")
                  : EMPTY_STRING,
              },
            ]}
          />
        )}
        {record.adHocCollaborator ? (
          <TextBoxGrid
            numCol={3}
            items={[
              {
                label: RECIPIENT_LABEL,
                text: record.collaborator
                  ? `${record.collaborator?.surname} ${record.collaborator?.name}`
                  : EMPTY_STRING,
              },
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.executionModes,
                  record.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.url,
              },
            ]}
          />
        ) : (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.executionModes,
                  record.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.url,
              },
            ]}
          />
        )}
        <TextBox label={DESCRIPTION_LABEL} text={record.description} />
      </>
    );
  };

  const rowKey = (record: Course) => {
    return record.id.toString();
  };

  useEffect(() => {
    setPageNumber(0);
  }, [selectedCategories, searchDebouncedValue]);

  return (
    <Modal
      label={ADD_COURSE_LABEL}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          onClick={() => coursesSaved.length > 0 && confirmModal(coursesSaved)}
          disabled={coursesSaved.length === 0}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={handleCloseModal}
        />
      }
    >
      <StyledFormationCM $isModal>
        <div className="cm-formation-header-container">
          <DoubleStructure
            verticalAlignment={AlignmentEnum.CENTER}
            variant="primary"
            wrap="end"
          >
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {`${data?.pagination.total ?? 0} ${data && data.pagination.total === 1 ? COURSE_LABEL : COURSES_LABEL}`}
            </BodyTyp>
            <div className="filters">
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                label={NAME_LABEL}
                name={"search"}
              />
              <Select
                variant={SelectVariantEnum.MULTI_SELECT}
                label={CATEGORY_LABEL}
                options={orgSettings.courses.categories}
                onChange={setSelectedCategories}
                value={selectedCategories}
              />
            </div>
          </DoubleStructure>
        </div>
        <TableFormationCM<Course>
          data={data?.data || []}
          loading={isFetching}
          scrollY={"40vh"}
          pagination={{
            current: pageNumber,
            onChange: setPageNumber,
            pageSize: COURSES_PAGE_SIZE,
            total: data?.pagination.total ?? 0,
          }}
          headers={getCollaboratorCoursesModalColumns({
            orgSettings,
          })}
          rowKey={rowKey}
          expandable={{
            expandedRowRender: expandRow,
          }}
          emptyText={EMPTY_COURSES_MESSAGE}
          onCheckbox={handleRowSelect}
          onSelectAll={handleSelectAll}
          selectedRows={coursesSaved}
        />
      </StyledFormationCM>
    </Modal>
  );
};
